import React, { useMemo, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { RoICalculatorV2 } from '@pinpoint/onpoint-charts';
import isEqual from 'lodash.isequal';
import debounce from 'lodash.debounce';
import camelCase from 'lodash.camelcase';
import get from 'lodash.get';
import { Buffer } from 'buffer';

import CONSTANTS from 'src/store/constants';
import { isValidNumber, calcEqPersonsAdj, calcEqPremiumsAdj } from 'src/functions';
import { ModelProp, RoiCalcStateProp } from 'src/store/typings';

const ACT_MODEL_TYPES = CONSTANTS.TAL_MODEL.TAGS.slice(2);

const isInputValid = ({ hasBaselineModel: hasBase, acquisitionDistributionFrac, ...rest }) =>
  Object.values(rest).every(isValidNumber);

export default function RoiCalculator({
  app: {
    profile: { tenant, email },
  },
  location: { pathname, search: queryParams },
  history,
  modelView: {
    equalType,
    dataset,
    model: { id: modelId, dataModelType, predictionType, typeDetail, modelData, payload },
    roiCalculatorState: {
      acquisitionDistribution,
      acquisitionRate,
      averagePremium,
      baselineLift,
      carrierNonRenewalRate,
      combinedLift,
      hasBaselineModel,
      policiesInForce,
      riskCost,
      yearlyCancellationRate,
    },
    disableInput,
  },
  resetRoiCalcStateField,
  updateRoiCalcState,
  initRoiCalcState,
  saveRoiCalcState,
  setReadyExport,
}) {
  const isLegacy = ['LIFT_VERSION_1_ACT', null, undefined].includes(payload?.version);
  const isActuarialModel = ACT_MODEL_TYPES.includes(dataModelType);
  const [shouldInit, setShouldInit] = useState(true);
  const liftScores = useMemo(() => {
    if ((isActuarialModel && isLegacy) || !isActuarialModel) return get(modelData, 'dataPayload.lift_scores', []);
    if (!isLegacy && equalType === null) return [];
    if (equalType === 'premiums')
      return get(
        modelData,
        `dataPayload.equal_premiums.lift_scores`,
        get(modelData, `dataPayload.equal_premium.lift_scores`)
      );
    if (equalType === 'persons') return get(modelData, `dataPayload.equal_persons.lift_scores`);
    return get(modelData, 'dataPayload.lift_scores', []);
  }, [modelData, isActuarialModel, isLegacy, equalType]);
  const [lastCalcUpdate, setLastCalcUpdate] = useState({});
  const liftScore = useMemo(
    () => liftScores.find((info) => get(info, isActuarialModel ? 'p_lift' : 'lift').length === 10),
    [liftScores, isActuarialModel]
  );
  const saveCalculator = useCallback(
    ({ input, version }) => {
      if (!isEqual(lastCalcUpdate, input) && isInputValid(input)) {
        saveRoiCalcState({
          thinkalikeModelId: modelId,
          state: { version, ...input },
          stateMetadata: { email, tenant },
        });
        setLastCalcUpdate(input);
      }
    },
    [lastCalcUpdate, modelId, email, tenant, saveRoiCalcState]
  );
  const saveCalcDebounced = useMemo(() => debounce(saveCalculator, 750), [saveCalculator]);
  const actuarialExtraProps = useMemo(() => {
    const { pinpointPIFPercentile, pinpointLosses, pinpointPremiums } =
      equalType === 'persons'
        ? calcEqPersonsAdj(
            get(liftScore, 'p_pif_percentile', []),
            get(liftScore, 'p_risk_cost', []),
            get(liftScore, 'p_premium', [])
          )
        : calcEqPremiumsAdj(
            get(liftScore, 'p_pif_percentile', []),
            get(liftScore, 'p_risk_cost', []),
            get(liftScore, 'p_premium', [])
          );

    return {
      baselineLift: get(liftScore, 'b_lift', baselineLift) || [],
      baselineLosses: get(liftScore, 'b_risk_cost', []),
      baselinePremiums: get(liftScore, 'b_premium', []),
      baselinePIFPercentile: get(liftScore, 'b_pif_percentile'),
      pinpointLosses,
      pinpointPremiums,
      pinpointPIFPercentile,
    };
  }, [liftScore, baselineLift, equalType]);

  useEffect(() => {
    // NOTE: update the calculators initial state from queryParams
    const query = new URLSearchParams(queryParams);
    let strB64 = query.get('state') || 'e30K';
    strB64 = strB64.replace(/-/g, '+').replace(/_/g, '/');
    if (strB64 !== 'e30K') {
      const bufferB64 = Buffer.from(strB64, 'base64');
      let stateStr = bufferB64.toString('utf8');
      stateStr = stateStr.replace('permium', 'premium'); // NOTE: needed to patch bad DB entry
      const paramState = JSON.parse(stateStr);
      const camelizedState = {};
      Object.keys(paramState).forEach((key) => {
        camelizedState[camelCase(key)] = paramState[key];
      });
      initRoiCalcState({ ...JSON.parse(CONSTANTS.TAL_MODEL.ROI_CALC_DEFAULTS), ...camelizedState });
      query.set('state', 'e30K');
      history.push({ pathname, search: query.toString() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  useEffect(() => {
    const seed = dataset?.payload?.roi_calculator_seed;
    const query = new URLSearchParams(queryParams);
    const strB64 = query.get('state') || 'e30K';
    if (seed && isActuarialModel && strB64 === 'e30K' && typeDetail && shouldInit) {
      const multiplier = typeDetail.includes('PERSONAL AUTO') ? 2 : 1;
      initRoiCalcState({
        ...JSON.parse(CONSTANTS.TAL_MODEL.ROI_CALC_DEFAULTS),
        yearlyCancellationRate: seed?.cancel_rate,
        acquisitionRate: seed?.new_business_rate,
        carrierNonRenewalRate: seed?.non_renewal_rate,
        policiesInForce: seed?.policies_in_force,
        averagePremium: (seed?.avg_yearly_premium ?? 0) * multiplier,
        riskCost: (seed?.avg_yearly_direct_loss_cost ?? 0) * multiplier,
      });
      setShouldInit(false);
    }
  }, [isActuarialModel, dataset, initRoiCalcState, typeDetail, queryParams, shouldInit]);

  return (
    <RoICalculatorV2
      modelType={dataModelType}
      riskModel={isActuarialModel ? CONSTANTS.TAL_MODEL.TAG[dataModelType] : predictionType}
      riskModelDetail={typeDetail}
      pinpointLift={get(liftScore, isActuarialModel ? 'p_lift' : 'lift', [])}
      onChange={updateRoiCalcState}
      onSave={saveCalcDebounced}
      acquisitionDistribution={acquisitionDistribution}
      acquisitionRate={acquisitionRate}
      averagePremium={averagePremium}
      baselineLift={baselineLift}
      carrierNonRenewalRate={carrierNonRenewalRate}
      combinedLift={combinedLift}
      hasBaselineModel={hasBaselineModel || isActuarialModel}
      policiesInForce={policiesInForce}
      riskCost={riskCost}
      yearlyCancellationRate={yearlyCancellationRate}
      disableInput={disableInput}
      resetAcquisitionDistribution={() => resetRoiCalcStateField('acquisitionDistribution')}
      setReadyExport={setReadyExport}
      {...actuarialExtraProps}
    />
  );
}

RoiCalculator.propTypes = {
  history: PropTypes.shape({ push: PropTypes.func.isRequired }).isRequired,
  location: PropTypes.shape({ search: PropTypes.string.isRequired, pathname: PropTypes.string.isRequired }).isRequired,
  app: PropTypes.shape({
    profile: PropTypes.shape({ tenant: PropTypes.string.isRequired, email: PropTypes.string.isRequired }).isRequired,
  }).isRequired,
  modelView: PropTypes.shape({
    equalType: PropTypes.string.isRequired,
    disableInput: PropTypes.bool.isRequired,
    dataset: PropTypes.oneOf([
      undefined,
      PropTypes.shape({
        payload: PropTypes.shape({}),
      }),
    ]).isRequired,
    model: ModelProp.isRequired,
    roiCalculatorState: RoiCalcStateProp.isRequired,
  }).isRequired,
  initRoiCalcState: PropTypes.func.isRequired,
  resetRoiCalcStateField: PropTypes.func.isRequired,
  saveRoiCalcState: PropTypes.func.isRequired,
  updateRoiCalcState: PropTypes.func.isRequired,
  setReadyExport: PropTypes.func.isRequired,
};
